import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { RowData } from "../../Interfaces/interfaces";
import VideoIdButtons from "../Buttons/videoIdButtons";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import ThirdPartyClaimDetails from "../Dialogs/thirdPartyClaimDetailsDialog";
import SyncThirdPartyClaimDetails from "../Dialogs/syncThirdPartyClaimDetailsDialog";
import "./tables.css";
import { includes } from "lodash";
import { isThirdPartyClaimPresent, resolveScorecardAssetId } from "../Utils/claimsUtils";
import { isMidrollMetricIncorrect } from "../../Objects/filterOptions";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../../config/tableLayout";

interface ClaimsAdsTableProps {
  rows: RowData[];
  loading: boolean;
  getPrivacy: any;
  transformRowForExport: (row: any) => any;
  lastUpdated: string | number | Date;
}

const ClaimsAdsTable: React.FC<ClaimsAdsTableProps> = ({
  rows,
  getPrivacy,
  transformRowForExport,
  lastUpdated,
}) => {
  const [selectedRowData, setSelectedRowData] = useState<RowData | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [syncDialogVisible, setSyncDialogVisible] = useState(false);
  const [rowInFocus, setRowInFocus] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useRef<Toast>(null);

  const formatDateTime = (dateString: string | number | Date) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
      .format(date)
      .replace(",", "");
  };

  const formattedDate = lastUpdated ? formatDateTime(lastUpdated) : "N/A";

  const isClaimPresent = (rowData: RowData) =>
    isThirdPartyClaimPresent(rowData);

  const isSyncClaimPresent = (rowData: RowData) => {
    if (rowData.syncThirdPartyClaim === "Yes" ) {
      return true;
    }
    if (!rowData.syncThirdPartyClaims || rowData.syncThirdPartyClaims === null || rowData.syncThirdPartyClaims === "" || rowData.syncThirdPartyClaim === "Whitelisted") {
      return false;
    }
    
    // Check if it's an empty array or stringified empty array
    if (Array.isArray(rowData.syncThirdPartyClaims) && rowData.syncThirdPartyClaims.length === 0) {
      return false;
    }
    
    // If it's a string, try to parse and check if it's an empty array
    if (typeof rowData.syncThirdPartyClaims === "string") {
      try {
        const parsed = JSON.parse(rowData.syncThirdPartyClaims);
        if (Array.isArray(parsed) && parsed.length === 0) {
          return false;
        }
      } catch (e) {
        // If parsing fails, treat as invalid/empty
        return false;
      }
    }
    
    return true;
  };

  const items = Array.from({ length: 5 }, (v, i) => i);
  const skeletonRows = items.map(() => ({}));
  
  const detailsButton = (rowData: RowData) => {
    //Simplified
    return (
      isClaimPresent(rowData) && (
        <Button
          icon="pi pi-info-circle"
          rounded
          text
          severity="secondary"
          className="third-party-info-button"
          style={{
            padding: "3px",
            margin: "3px",
          }}
          onClick={() => {
            setSelectedRowData(rowData);
            setDialogVisible(true);
          }}
        />
      )
    );
  };

  const syncDetailsButton = (rowData: RowData) => {
    return (
      isSyncClaimPresent(rowData) && (
        <Button
          icon="pi pi-info-circle"
          rounded
          text
          severity="secondary"
          className="sync-third-party-info-button"
          style={{
            padding: "3px",
            margin: "3px",
          }}
          onClick={() => {
            setSelectedRowData(rowData);
            setSyncDialogVisible(true);
          }}
        />
      )
    );
  };
  useEffect(() => {
    if (!rows) {
      setIsLoading(true);
    } else if (rows.length > 0) {
      setIsLoading(false);
    }
  }, [rows]);

  const paginatorTemplate = {
    layout:
      "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown", // Add CurrentPageReport to the end
    CurrentPageReport: (options: any) => (
      <div className="flex align-items-center">
        <span
          style={{
            color: "var(--text-color)",
            userSelect: "none",
            textAlign: "center",
          }}
          className="mx-3"
        >
          Showing {options.first} to {options.last} of {options.totalRecords}{" "}
          entries
        </span>
      </div>
    ),
  };

  const lastUpdatedTemplate = () => (
    <div className="flex align-items-center">
      <text
        style={{
          color: "var(--text-color)",
          userSelect: "none",
          textAlign: "center",
        }}
        className="mx-3"
      >
        Last Updated: {formattedDate}
      </text>
    </div>
  );

  return (
    <>
      {isLoading === true ? (
        <DataTable
          value={skeletonRows}
          size="normal"
          paginator
          rows={50}
          paginatorTemplate={
            "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink  RowsPerPageDropdown"
          }
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries" // Changed the template
          paginatorLeft
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
          showGridlines
          cellSelection
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortMode="multiple"
          selectionMode="single"
          onCellClick={(e) => {
            setRowInFocus(e.rowIndex);
            navigator.clipboard.writeText(e.value);
            toast.current?.show({
              severity: "info",
              summary: "Copied to Clipboard",
              detail: e.value,
              life: 1000,
            });
          }}
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator" // Add the custom class here
          style={{
            marginBottom: "-3.3rem",
            marginRight: "-1rem",
            marginLeft: "-1rem",
          }}
        >
          {/*create as many columns as needed*/}
          <Column field="videoId" header="Video ID" body={() => <Skeleton />} />
          <Column header="URL" body={() => <Skeleton />} />
          <Column header="Privacy" body={() => <Skeleton />} />
          <Column
            header="CMS"
            body={() => <Skeleton />}
            style={{ width: "10%" }}
          />
          <Column header="Content Format" body={() => <Skeleton />} />
          <Column header="Channel Name" body={() => <Skeleton />} />
          <Column header="Channel ID" body={() => <Skeleton />} />
          <Column
            header="Upload Date"
            body={() => <Skeleton />}
            style={{ width: "10%" }}
          />
          <Column
            header="Published Date"
            body={() => <Skeleton />}
            style={{ width: "10%" }}
          />
          <Column
            header="Scheduled Date"
            body={() => <Skeleton />}
            style={{ width: "10%" }}
          />
          <Column
            header="Channel Manager"
            body={() => <Skeleton />}
            style={{ width: "5%" }}
          />
          <Column header="Channel Manager Email" body={() => <Skeleton />} />
          <Column header="Legal Entity Type" body={() => <Skeleton />} />
          <Column header="Legal Entity Name" body={() => <Skeleton />} />
          <Column header="Legal Entity" body={() => <Skeleton />} />
          <Column
            header="Video Title"
            body={() => <Skeleton />}
            style={{ width: "10%" }}
          />
          <Column
            header="Views"
            body={() => <Skeleton />}
            style={{ textAlign: "right" }}
          />
          <Column header="Custom ID" body={() => <Skeleton />} />
          <Column header="Asset ID" body={() => <Skeleton />} />
          <Column header="Asset Format" body={() => <Skeleton />} />
          <Column header="Asset Owned" body={() => <Skeleton />} />
          <Column header="3rd Party Claim" body={() => <Skeleton />} />
          <Column header="Sync Composition Claims" body={() => <Skeleton />} />
          <Column header="Moonbug Claim" body={() => <Skeleton />} />
          <Column header="Skippable Ads" body={() => <Skeleton />} />
          <Column header="Non-Skippable Ads" body={() => <Skeleton />} />
          <Column header="Pre-Rolls" body={() => <Skeleton />} />
          <Column header="Mid-Rolls" body={() => <Skeleton />} />
          <Column header="Post-Roll" body={() => <Skeleton />} />
          <Column
            header="Midroll Frequency"
            body={() => <Skeleton />}
            style={{ width: "20%" }}
          />
          <Column
            header="Largest Midroll Interval"
            body={() => <Skeleton />}
            style={{ width: "20%" }}
          />
        </DataTable>
      ) : (
        <DataTable
          id="claims-ads-table"
          value={rows.length > 0 ? rows : []}
          size="normal"
          paginator
          rows={50}
          paginatorTemplate={paginatorTemplate}
          paginatorLeft={lastUpdatedTemplate()}
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
          showGridlines
          cellSelection
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortMode="multiple"
          selectionMode="single"
          onCellClick={(e) => {
            setRowInFocus(e.rowIndex);
            navigator.clipboard.writeText(e.value);
            toast.current?.show({
              severity: "info",
              summary: "Copied to Clipboard",
              detail: e.value,
              life: 1000,
            });
          }}
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator" // Add the custom class here
          style={{
            marginBottom: "-3.3rem",
            marginRight: "-1rem",
            marginLeft: "-1rem",
          }}
        >
          {rows.length === 0 && (
            <tr>
              <td colSpan={Column.length} style={{ textAlign: "center" }}>
                No matching rows found.
              </td>
            </tr>
          )}

          <Column
            field="videoId"
            header="Video ID"
            headerTooltip="The unique YouTube ID that identifies the video"
            headerTooltipOptions={{
              position: "right",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            header="URL"
            body={(rowData) => <VideoIdButtons rowData={rowData} />}
          />
          <Column
            field="privacy"
            header="Privacy"
            headerTooltip="The privacy status of the video"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            sortable
            body={(rowData) => {
              const backgroundColor =
                rowData.privacy === "Scheduled"
                  ? "orange"
                  : rowData.privacy === "Public"
                  ? "green"
                  : rowData.privacy === "Private"
                  ? "blue"
                  : "red";

              return (
                <div
                  style={{
                    backgroundColor,
                    color: "white",
                    textAlign: "center",
                    padding: "5px",
                    borderRadius: "4px",
                  }}
                >
                  {getPrivacy(rowData)}
                </div>
              );
            }}
          />
          <Column
            field="CMS"
            headerTooltip="The internal name of the YouTube CMS that the video and channel are connected to"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="CMS"
            style={{ width: "10%" }}
            sortable
            body={(rowData) => {
              if (rowData.CMS === "Missing") {
                return <span>{rowData.CMS}</span>;
              }
              return rowData.CMS || "";
            }}
            bodyClassName={(rowData: RowData) => {
              switch (rowData.CMS) {
                case "Missing":
                  return "incorrect";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="CreatorContentType"
            headerTooltip="Specifies if the content is a regular YouTube video, a Short or a Livestream"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Content Format"
            sortable
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="Channel_Name"
            headerTooltip="The name of the YouTube channel that the video is uploaded to"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Channel Name"
            sortable
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="YoutubeChannelId"
            headerTooltip="The unique YouTube ID that identifies the YouTube channel the video is uploaded to"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Channel ID"
            sortable
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="timeUploaded"
            headerTooltip="The date that the video was uploaded to YouTube (UTC)"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Upload Date"
            style={{ width: "10%" }}
            sortable
            body={(rowData) => {
              const date = new Date(rowData.timeUploaded);
              return isNaN(date.getTime())
                ? "Invalid Date"
                : date.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  });
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="timePublished"
            headerTooltip="The date that the video was published to YouTube (UTC)"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Published Date"
            style={{ width: "10%" }}
            sortable
            body={(rowData) => {
              const date = new Date(rowData.timePublished);
              return isNaN(date.getTime())
                ? "N/A"
                : date.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  });
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="PublishDate"
            headerTooltip="The date that the video is scheduled to be published to YouTube (UTC)"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Scheduled Date"
            style={{ width: "10%" }}
            sortable
            body={(rowData) => {
              const date = new Date(rowData.PublishDate);
              return isNaN(date.getTime())
                ? "N/A"
                : date.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  });
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="ChannelManagerName"
            headerTooltip="The name/s of the channel manager/s in charge of the YouTube Channel"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Channel Manager"
            sortable
            body={(rowData) => {
              if (rowData.ChannelManagerName === "Missing") {
                return <span>{rowData.ChannelManagerName}</span>;
              }

              if (
                rowData.ChannelManagerName &&
                rowData.ChannelManagerName.includes(",")
              ) {
                const emails = rowData.ChannelManagerName.split(",").map(
                  (email: string) => email.trim()
                );
                return <span>{emails.join("\n")}</span>;
              }
              return rowData.ChannelManagerName || "";
            }}
            bodyClassName={(rowData) => {
              switch (rowData.ChannelManagerName) {
                case "Missing":
                  return "status-cell incorrect";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
            style={{ width: "5%" }}
          />
          <Column
            field="ChannelManagerEmail"
            headerTooltip="The email/s of the channel manager/s in charge of the YouTube Channel"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Channel Manager Email"
            sortable
            body={(rowData) => {
              if (rowData.ChannelManagerEmail === "Missing") {
                return <span>{rowData.ChannelManagerEmail}</span>;
              }

              if (
                rowData.ChannelManagerEmail &&
                rowData.ChannelManagerEmail.includes(",")
              ) {
                const emails = rowData.ChannelManagerEmail.split(",").map(
                  (email: string) => email.trim()
                );
                return <span>{emails.join("\n")}</span>;
              }
              return rowData.ChannelManagerEmail || "";
            }}
            bodyClassName={(rowData) => {
              switch (rowData.ChannelManagerEmail) {
                case "Missing":
                  return "status-cell incorrect";

                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="LegalEntityType"
            headerTooltip="The legal entity type associated with the channel"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Legal Entity Type"
            sortable
            body={(rowData) => <span>{rowData.LegalEntityType || "Missing"}</span>}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="LegalEntityName"
            headerTooltip="The legal entity name associated with the channel"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Legal Entity Name"
            sortable
            body={(rowData) => <span>{rowData.LegalEntityName || "Missing"}</span>}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="LegalEntity"
            headerTooltip="The legal entity associated with the channel"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Legal Entity"
            sortable
            body={(rowData) => <span>{rowData.LegalEntity || "Missing"}</span>}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="videoTitle"
            headerTooltip="The title of the video on YouTube"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Video Title"
            sortable
            body={(rowData) => {
              return <span>{rowData.videoTitle || "Missing"}</span>;
            }}
            bodyClassName={(rowData) => {
              switch (rowData.videoTitle) {
                case "Missing":
                  return "status-cell incorrect";

                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
            style={{ width: "10%" }}
          />
          <Column
            field="views"
            headerTooltip="The total number of views that the video has generated in its entire lifetime"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Views"
            sortable
            style={{ textAlign: "right" }}
            body={(rowData) =>
              rowData.views == null ? "" : Number(rowData.views).toLocaleString()
            }
          />
          <Column
            field="customid"
            headerTooltip="The internal code assigned to the video by the channel manager to identify it in the catalog and the correctness status"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Custom ID"
            sortable
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="assetId"
            headerTooltip="The unique YouTube ID that identifies the Asset ID of the asset claiming the video"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Asset ID"
            sortable
            body={(rowData) => <span>{resolveScorecardAssetId(rowData)}</span>}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="assetFormat"
            headerTooltip="Format of the asset"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Asset Format"
            sortable
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="ownership"
            headerTooltip="Check whether the asset is Moonbug-owned globally"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Asset Owned"
            sortable
            body={(rowData) => <span>{rowData.ownership}</span>}
            bodyClassName={(rowData) => {
              switch (rowData.ownership) {
                case "No":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="thirdPartyClaim"
            headerTooltip="Checks whether the video has an active Content ID claim from another entity"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="3rd Party Claim"
            sortable
            className="third-party-claim-column"
            body={(rowData) => (
              <span>
                <div className="flex items-center">
                  <p>{isClaimPresent(rowData) ? "Yes" : rowData.thirdPartyClaim === "Whitelisted" ? "Whitelisted" : "No"}</p>
                  {detailsButton(rowData)}
                </div>
              </span>
            )}
            bodyClassName={(rowData) => {
              // Only mark as incorrect if claims are actually present
              if (isClaimPresent(rowData)) {
                return "status-cell incorrect";
              } else if (rowData.thirdPartyClaim === "Whitelisted") {
                return "status-cell whitelisted";
              }
              return "";
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="syncThirdPartyClaims"
            headerTooltip="Checks whether the video has an active sync composition claim from another entity"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Sync Composition Claims"
            sortable
            className="sync-third-party-claim-column"
            body={(rowData) => (
              <span>
                <div className="flex items-center">
                  <p>{isSyncClaimPresent(rowData) ? "Yes" : "No"}</p>
                  {syncDetailsButton(rowData)}
                </div>
              </span>
            )}
            bodyClassName={(rowData) => {
              if (isSyncClaimPresent(rowData)) {
                return "status-cell incorrect";
              }
              return "";
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="claimStatus"
            headerTooltip="Checks whether the video has been both claimed and monetized by Moonbug"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Moonbug Claim"
            sortable
            body={(rowData) => <span>{rowData.claimStatus}</span>}
            bodyClassName={(rowData) => {
              switch (rowData.claimStatus) {
                case "No":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="skippableAdsEnabled"
            headerTooltip="Checks whether the skippable ad format has been enabled for the video"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Skippable Ads"
            sortable
            body={(rowData) => <span>{rowData.skippableAdsEnabled}</span>}
            bodyClassName={(rowData) => {
              switch (rowData.skippableAdsEnabled) {
                case "No":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="nonskippableVideoAdsEnabled"
            headerTooltip="Checks whether the non-skippable ad format has been enabled for the video"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Non-Skippable Ads"
            sortable
            body={(rowData) => (
              <span>{rowData.nonskippableVideoAdsEnabled}</span>
            )}
            bodyClassName={(rowData) => {
              switch (rowData.nonskippableVideoAdsEnabled) {
                case "No":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="preroll_count"
            headerTooltip="Checks whether pre-roll ads have been enabled"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Pre-Rolls"
            sortable
            body={(rowData) => <span>{rowData.preroll_count}</span>}
            bodyClassName={(rowData) => {
              switch (rowData.preroll_count) {
                case "No":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="midroll_count"
            headerTooltip="Checks whether mid-roll ads have been enabled for videos longer than 8 minutes"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Mid-Rolls"
            sortable
            body={(rowData) => <span>{rowData.midroll_count}</span>}
            bodyClassName={(rowData) => {
              switch (rowData.midroll_count) {
                case "No":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="postroll_count"
            headerTooltip="Checks whether post-roll ads have been enabled"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Post-Roll"
            sortable
            body={(rowData) => <span>{rowData.postroll_count}</span>}
            bodyClassName={(rowData) => {
              switch (rowData.postroll_count) {
                case "No":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="full_midroll_timestamp"
            headerTooltip="Checks whether mid-roll frequency is acceptable (target 2–6 min, ±10% tolerance)"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Midroll Frequency"
            sortable
            style={{ width: "20%" }}
            body={(rowData) => {
              const interval = rowData.full_midroll_timestamp;
              let displayText;

              if (interval === null) {
                displayText = "No Ad Data";
              } else if (interval === "N/A") {
                displayText = `N/A`;
              } else if (interval === "Whitelisted") {
                displayText = `${interval}`;
              } else if (typeof interval === "number") {
                displayText = `Average frequency of ads: ${interval} min`;
              } else {
                displayText = "No Ad Data";
              }
              return <span>{displayText}</span>;
            }}
            bodyClassName={(rowData) => {
              const interval = rowData.full_midroll_timestamp;
              if (isMidrollMetricIncorrect(interval)) {
                return "status-cell incorrect";
              }
              if (interval === "Whitelisted") {
                return "status-cell whitelisted";
              }
              return "";
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="biggest_midroll_interval"
            headerTooltip="Checks whether the largest mid-roll interval is acceptable (target 2–6 min, ±10% tolerance)"
            headerTooltipOptions={{
              position: "left",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Largest Midroll Interval"
            sortable
            style={{ width: "20%" }}
            body={(rowData) => {
              const interval = rowData.biggest_midroll_interval;
              let displayText;

              if (interval === null) {
                displayText = "No Ad Data";
              } else if (interval === "N/A") {
                displayText = `N/A`;
              } else if (interval === "Whitelisted") {
                displayText = `${interval}`;
              } else if (isMidrollMetricIncorrect(interval)) {
                displayText = `Unacceptable interval: ${interval} min`;
              } else if (typeof interval === "number") {
                displayText = `Acceptable interval: ${interval} min`;
              } else {
                displayText = "No Ad Data";
              }

              return <span>{displayText}</span>;
            }}
            bodyClassName={(rowData) => {
              const interval = rowData.biggest_midroll_interval;
              if (isMidrollMetricIncorrect(interval)) {
                return "status-cell incorrect";
              }
              if (interval === "Whitelisted") {
                return "status-cell whitelisted";
              }
              return "";
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
          <Column
            field="auto_generated_breaks"
            headerTooltip="Checks whether auto generated ads have been enabled"
            headerTooltipOptions={{
              position: "top",
              style: { maxWidth: "200px", fontSize: "0.8rem" },
            }}
            header="Auto Generated Ads"
            sortable
            body={(rowData) => <span>{rowData.auto_generated_breaks}</span>}
            bodyClassName={(rowData) => {
              switch (rowData.auto_generated_breaks) {
                case "Off":
                  return "status-cell incorrect";
                case "Whitelisted":
                  return "status-cell whitelisted";
                default:
                  return "";
              }
            }}
            bodyStyle={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              justifyItems: "center",
            }}
          />
        </DataTable>
      )}

      <ThirdPartyClaimDetails
        rowData={selectedRowData}
        visible={dialogVisible}
        onHide={() => {
          setDialogVisible(false);
          setSelectedRowData(null);
        }} // Clear selectedRowData when hiding
      />
      <SyncThirdPartyClaimDetails
        rowData={selectedRowData}
        visible={syncDialogVisible}
        onHide={() => {
          setSyncDialogVisible(false);
          setSelectedRowData(null);
        }} // Clear selectedRowData when hiding
      />
      <Toast ref={toast} />
    </>
  );
};

export default ClaimsAdsTable;
